/**
 * Der Reiter „EK" (Landed Cost) des Bestell-Drawers - Portierung von `drawerCalcTab`
 * (`index.html:8724-8766`), `ekaControlPanel` (`:8768-8814`) und `renderCalc` (`:10380-10398`).
 * Er haengt sich als `children` in `.drawer-body` des Rahmens aus Task 16, wie `BestellungTab`
 * (Task 17) und `TasksTab` (Task 18).
 *
 * Aufbau, in der Reihenfolge des Originals:
 *
 *   .calc-hero                              Kopfzeile samt „In JTL übertragen"
 *     .calc-note.warn (bedingt)             ABWEICHUNG vom Bestand (Befund 3, Option C, 2026-08-11):
 *                                            Warnhinweis bei nicht zugeordneter FOC-Menge - der
 *                                            Bestand kennt diesen Hinweis nicht, siehe
 *                                            `verloreneFocMenge` unten.
 *   .eka-control-panel                      `ekaControlPanel` - die automatische Dokumentkontrolle
 *   sec('calc-items', 'EK je Artikel')
 *     .field-grid.calc-source-grid          die vier Bestellsummen (`patchCalc`)
 *     .calc-note                            Verteilungsregel
 *     #calcItems                            `PositionsTabelle` bzw. `.empty-note` (`renderCalc`)
 *     .calc-note                            FOC-Regel
 *
 * `.calc-wide` am `aside#drawer` setzt bereits `Drawer.tsx` (`index.html:9254`,
 * `tab === "calc"`) - dieser Reiter braucht dafuer nichts zu tun.
 *
 * ================= Das calcEk-Ergebnis bleibt LOKAL (Auffaelligkeit 21) =================
 *
 * `calcEk` verbindet Zeile und Tarifgruppe in BEIDE Richtungen: `group.rows.push(row)`
 * (`index.html:10175`) und `row.customsGroup = group` (`:10192-10193`). Das Ergebnis ist damit
 * zyklisch. Fuer die Oberflaeche folgenlos, fuer jede Serialisierung ein Stolperstein:
 * `JSON.stringify` wirft daran, `structuredClone` ueber eine Worker-Grenze ebenso wenig harmlos
 * (es kopiert den Zyklus mit und macht ihn damit zum Dauerbegleiter des Stores). Deshalb gilt hier
 * eine harte Regel:
 *
 *   Das Ergebnis von `calcEk` wird bei JEDEM Zeichnen neu berechnet (`useMemo`) und verlaesst diese
 *   Komponente NIEMALS - nicht in den Store, nicht nach `localStorage`, nicht durch `onAendern`.
 *
 * Nach `onAendern` gehen ausschliesslich frisch gebaute Aenderungsobjekte (`{ items: [...] }`,
 * `{ freightInvoice: "…" }`) - flache Datenwerte ohne Bezug auf `ergebnis`. Ein Test haelt das fest
 * (`test/komponenten/Drawer/ekTab.test.tsx`).
 *
 * ================= `refreshCalcDerived` hat hier keinen Gegenpart =================
 *
 * Der Bestand fasst nach jeder Eingabe die abgeleiteten Felder einzeln per `document.getElementById`
 * nach (`index.html:10327-10378`), damit der Eingabefokus stehen bleibt; `onblur="refreshCalcDerived()"`
 * ist derselbe Nachzug beim Verlassen eines Feldes. In React fliesst jede Aenderung durch
 * `onAendern` in den Store, der Reiter wird neu gezeichnet und `useMemo` rechnet neu - der
 * Nachzug entfaellt vollstaendig, das Ergebnis ist dasselbe. GEMELDETE FOLGE: der Bestand zeigt die
 * Mengenherkunft beim ERSTEN Zeichnen als „PL + FOC" (`ekTableRow`, `:10274`), nach dem ersten
 * Nachzug aber als „Packing List + FOC" (`refreshCalcDerived`, `:10333`) - zwei Fassungen derselben
 * Beschriftung. Portiert ist die des Erstzeichnens; siehe Task-Bericht.
 *
 * ================= NICHT portiert (gemeldet) =================
 *
 * 1. `patchCalc` (`:9934`) traegt einen Sonderweg fuer `key === 'dutyPct'` samt `o.dutyPctLocked`.
 *    `patchCalc('dutyPct', …)` hat im gesamten Original KEINEN Aufrufer, und `lockDutyPct` (`:9947`),
 *    die einzige Stelle, die `dutyPctLocked` je auf `true` setzt, hat ebenfalls keinen. Der Zweig ist
 *    im Bestand unerreichbar. Er ist hier trotzdem nachgebildet (`bestellungPatchen`), damit ein
 *    spaeter nachgezogener Aufrufer dasselbe Verhalten findet - die Woertlichkeitsregel schlaegt die
 *    Aufraeumung.
 * 2. `transferToJtl` (`:9964`) zeigt im Bestand nur einen Hinweis („JTL-Übertragung ist vorbereitet -
 *    Funktion kommt noch"). Der Knopf haengt hier an `onJtlUebertragen`; ohne Rueckruf bleibt er
 *    wirkungslos - genau wie im Bestand ohne geoeffnete Bestellung.
 */
import { useMemo } from "react";
import { Abschnitt } from "../Abschnitt.tsx";
import { Datumsfeld } from "../Datumsfeld.tsx";
import { PositionsTabelle } from "./PositionsTabelle.tsx";
import { darfTabBearbeiten } from "../drawerRechte.ts";
import { calcEk } from "../../../lib/landedCost.ts";
import { schreibwegVon as positionsSchreibwegAus } from "../../../lib/positionsSchreibweg.ts";
import { EUR, NUM, fmtDate, todayIso } from "../../../lib/format.ts";
import { meldeFehler } from "../../../state/fehlerStapel.ts";
import type { CalcEkResult } from "../../../lib/landedCost.ts";
import type { FxDeal } from "../../../lib/fx.ts";
import type { Order, OrderDocument, OrderItem, Supplier } from "../../../lib/types.ts";

/** `index.html:8769` bzw. `:10049` - dieselbe Pruefung wie in `calcEk`, woertlich. */
const hasValue = (value: unknown): boolean =>
  value !== "" && value !== null && value !== undefined && Number.isFinite(Number(value));

/** `esc(s) = String(s ?? '')` (`index.html:4674`) - die HTML-Maskierung uebernimmt in React JSX. */
const roh = (value: unknown): string => String(value ?? "");

/** `index.html:8781-8782` - die Belegtypen, die die Kontrolle als „ausgelesen" zaehlt. */
const ZOLL_BELEGTYPEN = ["steuerbescheid", "zollrechnung"];
const TRANSPORT_BELEGTYPEN = ["speditionsrechnung", "speditionsgebuehren"];

/** `index.html:10050` - dieselbe Nichtnegativ-Klammer wie in `calcEk`, woertlich. */
const positive = (value: unknown): number => Math.max(0, Number(value) || 0);

/**
 * Befund 3, Option C (entschieden 2026-08-11, Auftraggeber): `calcEk` verliert eine FOC-Zeile
 * spurlos, wenn sie keine SKU findet UND ihre unmittelbare Vorgaengerzeile `manual` ist - der
 * Rueckfall prueft nur `!foc`, nicht `!manual` (`landedCost.ts:222-229`). Die Zeile bekommt dann
 * eine Verknuepfung auf einen Index, den `rows` gar nicht kennt; ihre Menge fehlt danach sowohl in
 * `focQtySum` als auch in `unassignedFocQty`, ohne jede Meldung.
 *
 * DIE RECHNUNG BLEIBT 1:1 - hier wird nur gezaehlt und verglichen. Die Mengenableitung ist
 * WOERTLICH die aus `calcEk` (`landedCost.ts:232-235`): `qtyPl` hat Vorrang, sonst `qty + focQty`.
 * Eine eigene Ableitung wuerde den Hinweis bei Bestellungen ausloesen, bei denen gar nichts fehlt.
 *
 * ================= Verglichen wird gegen die VERKNUEPFTE Menge, nicht gegen `focQtySum` =========
 *
 * Richtiggestellt am 2026-08-11 (Gesamtreview des Zweigs, Befund C1). `focQtySum` ist NICHT „die
 * zugeordnete FOC-Menge": es ist die Summe der GERECHNETEN `focQty` je Zeile
 * (`landedCost.ts:450`), und die durchlaeuft vorher die Uebersteuerungslogik
 * (`landedCost.ts:287-293`). Daran ging der Vergleich in beide Richtungen daneben:
 *
 * - FEHLALARM in der gewoehnlichen Bedienung. Setzt der Nutzer die FOC-Spalte einer Hauptzeile von
 *   Hand herunter (`focOverrideIsSet`), gilt die Handeingabe statt der zugeordneten Menge. Verloren
 *   ist dabei NICHTS - die Verknuepfung steht vollstaendig -, der Hinweis erschien trotzdem bei
 *   JEDER Korrektur nach unten.
 * - SCHWEIGEN im Zielfall. Traegt die Hauptzeile eine eigene `focQty` und hat sie KEINE Verknuepfung
 *   (weil die FOC-Zeile hinter einer `manual`-Zeile verschwunden ist), speist genau diese direkte
 *   Menge `focQtySum` - der Verlust war damit rechnerisch gedeckt und blieb unbemerkt.
 *
 * Verglichen wird deshalb gegen die tatsaechlich VERKNUEPFTE Menge: `linkedFocQty` je Zeile - das
 * ist woertlich `linkedFoc.reduce((s, link) => s + link.qty, 0)` (`landedCost.ts:284`), also die
 * Summe der Verknuepfungen, die eine Zeile wirklich erreicht haben - plus `unassignedFocQty` fuer
 * die ausdruecklich offen gebliebenen. Beides ist von der Uebersteuerung und von der direkten
 * `focQty` unberuehrt. Was in `gesamt` steht, aber in keiner der beiden Groessen auftaucht, ist
 * genau die auf einem zeilenlosen Index gestrandete Menge - der Verlust aus Befund 3.
 *
 * Liefert die verlorene Menge (> 0 heisst: Hinweis zeigen).
 */
function verloreneFocMenge(positionen: readonly OrderItem[], ergebnis: CalcEkResult): number {
  const gesamt = positionen.reduce((summe, it) => {
    if (!it?.foc) return summe;
    const qtyPlGesetzt = hasValue(it.qtyPl);
    return summe + (qtyPlGesetzt ? positive(it.qtyPl) : positive(it.qty) + positive(it.focQty));
  }, 0);
  const verknuepft = ergebnis.rows.reduce((summe, zeile) => summe + zeile.linkedFocQty, 0);
  const zugeordnet = verknuepft + ergebnis.unassignedFocQty;
  // Gleitkomma: die Summanden sind Mengen, aber `qtyPl` darf gebrochen sein. Beide Seiten summieren
  // dieselben Zahlen, aber in anderer Reihenfolge - die Klammer faengt das ab.
  return Math.max(0, Math.round((gesamt - zugeordnet) * 1e6) / 1e6);
}

export interface EkTabProps {
  bestellung: Order;
  /** `DB.fxDeals` - geht ueber `fxMix` in `calcEk` ein. */
  deals: readonly FxDeal[];
  /** `DB.suppliers` - geht ueber `orderCurrency` in `calcEk` ein. */
  lieferanten: readonly Supplier[];
  /**
   * `DB.orders`. `calcEk` braucht die Liste NICHT (siehe Kopfkommentar von `lib/landedCost.ts`);
   * sie steht in den Props, weil der Task-Brief sie hereinreicht und die Begleitfunktionen des
   * Moduls (`shipmentOrderGroup`, `consolidatedItems`) sie erwarten, sobald dieser Reiter sie
   * braucht. Heute liest der Reiter sie nicht - im Bestand tut das `drawerCalcTab` ebenso wenig.
   */
  bestellungen: readonly Order[];
  /** Der Rechtesatz des angemeldeten Kontos. Siehe `Ueberschreibungen.tsx`, Abschnitt „Rechte". */
  rechte: ReadonlySet<string>;
  onAendern: (id: string, aenderung: Record<string, unknown>) => void;
  /**
   * Schreibt EINE Position ueber `PATCH /api/orders/:id/items/:jtlPosId` (Task Positionsmodul,
   * 2026-08-13). Getrennt von `onAendern`, weil es eine andere Ressource mit einer EIGENEN
   * `rowVersion` ist: die Position traegt ihre Version selbst, nicht die Bestellung.
   *
   * Optional, damit die vorhandenen Tests, die `EkTab` ohne diesen Rueckruf zeichnen, weiterhin den
   * ALTEN (lauten) Weg messen statt still zu veraendern - siehe `positionPatchen` unten.
   *
   * `sortIndex` (Task 12, wortgleich zu `Positionen.tsx#PositionenProps.onPositionAendern`): der
   * Laufindex der Position in `positionen` - der Server ignoriert ihn, sobald die Zeile schon
   * existiert, er ist also bei JEDEM Aufruf gefahrlos. Bis Task 12 fehlte er hier komplett; eine neu
   * entstehende Zeile ohne ihn landete hinten statt an ihrem Platz in der JTL-Reihenfolge.
   */
  onPositionAendern?: (
    jtlPosId: number,
    aenderung: Record<string, unknown>,
    rowVersion: number,
    sortIndex: number,
  ) => void;
  /** `CURRENT_USER` - nur fuer `ekaReceivedDateConfirmedBy` (`index.html:8822`). */
  nutzer?: { displayName?: unknown; username?: unknown } | null;
  /** `transferToJtl()` (`index.html:8735`, `:9964`). */
  onJtlUebertragen?: () => void;
}

export function EkTab({
  bestellung: o,
  deals,
  lieferanten,
  rechte,
  onAendern,
  onPositionAendern,
  nutzer,
  onJtlUebertragen,
}: EkTabProps) {
  const id = o.id as string;

  /**
   * Siehe Kopfkommentar: neu berechnet bei jedem Zeichnen, NIE weitergegeben. Die
   * Abhaengigkeitsliste nennt genau die drei Argumente - `calcEk` liest sonst nichts.
   */
  const ergebnis: CalcEkResult = useMemo(
    () => calcEk(o, deals, lieferanten),
    [o, deals, lieferanten],
  );

  const qty = ergebnis.qtySum;
  const focQty = ergebnis.focQtySum;
  // Befund 3, Option C (2026-08-11) - siehe verloreneFocMenge.
  const focVerlust = verloreneFocMenge((o.items || []) as OrderItem[], ergebnis);
  const darfBearbeiten = darfTabBearbeiten(rechte, "calc");
  const positionen = (o.items || []) as OrderItem[];

  /** `patchCalc(key, value)` (`index.html:9934-9945`) - samt der unerreichbaren `dutyPct`-Sperre. */
  const bestellungPatchen = (schluessel: string, wert: unknown): void => {
    if (schluessel === "dutyPct" && o.dutyPctLocked) {
      // `toast(...)` des Bestands (`index.html:9938`) - jetzt am Stapel statt ueber die entfallene Prop `onHinweis`.
      meldeFehler("Zollsatz ist bereits festgelegt");
      return;
    }
    onAendern(id, { [schluessel]: wert });
  };

  /**
   * `patchItemCalc`/`patchItemCalcLive` (`index.html:9970`/`:9980`). Der Bestand mutiert
   * `o.items[i][key]` an Ort und Stelle; hier geht die vollstaendige Liste zurueck - derselbe Weg
   * wie in `Positionen.tsx` (Task 17), Begruendung dort. Das Aenderungsobjekt ist frisch gebaut und
   * enthaelt keinen Bezug auf `ergebnis` (siehe Kopfkommentar).
   *
   * SEIT DEM 2026-08-13 GEHT DIESER WEG WIRKLICH IRGENDWOHIN. Hier laufen SAEMTLICHE Positionsfelder
   * aus `PositionsTabelle.tsx`/`Ueberschreibungen.tsx`/`Zolltarifgruppen.tsx` zusammen, und bis dahin
   * endeten sie ausnahmslos im Riegel: `"items"` steht in KEINER Feldgruppe
   * (`api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG`), also warf `bestellungAendern` mit
   * `FeldOhneGruppeFehler`, BEVOR ueberhaupt etwas gesendet wurde - laut, aber wirkungslos: keine
   * Positionseingabe war je speicherbar. Jetzt schreibt `onPositionAendern` die EINZELNE Position
   * ueber `PATCH /api/orders/:id/items/:jtlPosId` (`api/positionen.ts`).
   *
   * DER SCHREIBWEG EINER POSITION - seit Task 12 GEMEINSAM mit `Positionen.tsx#schreibwegVon` in
   * `lib/positionsSchreibweg.ts`, wortgleich benutzt statt ein zweites Mal abgeleitet: siehe dort fuer
   * die vollstaendige Begruendung (zwei Quellen fuer die Positionskennung - `jtlPosId` ODER
   * `kLieferantenBestellungPos` -, Sentinel `0` fuer eine unbekannte `rowVersion`).
   *
   * BIS TASK 12 verlangte diese Stelle STRIKT eine bereits vorhandene `jtlPosId` UND eine ganzzahlige
   * `rowVersion` - ohne Rueckfall auf `kLieferantenBestellungPos` oder die Sentinel. Das traf genau
   * die 2132 Bestellungen, die (noch) keine `order_item_extras`-Zeile haben: der EK-Reiter fiel dort
   * auf den ALTEN, lauten Weg zurueck und schrieb nichts, obwohl `Positionen.tsx` dieselbe Position
   * seit Task 11 laengst ueber den Positionsweg haette speichern koennen.
   *
   * Faellt die Position weiterhin durch (gar keine JTL-Kennung ODER kein `onPositionAendern`), bleibt
   * es beim ALTEN Weg: weiterhin laut, weiterhin ohne Wirkung; er verschluckt also nichts, was sonst
   * angekommen waere, und eine ERFUNDENE Versionsnummer entsteht an keiner Stelle (genau die stille
   * Notloesung, die `App.tsx#uebersichtAufgabeUmschalten` fuer Aufgaben ausschliesst).
   */
  const schreibwegVon = (i: number): { jtlPosId: number; rowVersion: number } | null => {
    if (!onPositionAendern) return null;
    return positionsSchreibwegAus(positionen, i);
  };

  const positionPatchen = (i: number, aenderung: Record<string, unknown>): void => {
    const weg = schreibwegVon(i);
    if (weg) {
      onPositionAendern?.(weg.jtlPosId, aenderung, weg.rowVersion, i);
      return;
    }
    onAendern(id, {
      items: positionen.map((p, j) => (j === i ? { ...p, ...aenderung } : p)),
    });
  };

  /** `patchEkaReceivedConfirmed(checked)` (`index.html:8816-8825`). */
  const belegeingangBestaetigen = (gesetzt: boolean): void => {
    if (gesetzt && !o.ekaReceivedDate) {
      // `toast(...)` des Bestands (`index.html:8819`): die Bestaetigung wurde NICHT ausgefuehrt, also Stapel.
      meldeFehler("Bitte zuerst das Eingangsdatum prüfen");
      return;
    }
    const name = (nutzer?.displayName as string) || (nutzer?.username as string) || "Unbekannt";
    onAendern(id, {
      ekaReceivedDateConfirmed: !!gesetzt,
      ekaReceivedDateConfirmedAt: gesetzt ? new Date().toISOString() : "",
      ekaReceivedDateConfirmedBy: gesetzt ? name : "",
    });
  };

  // ---- ekaControlPanel (index.html:8768-8814), woertlich ----
  const articleCount = ergebnis.rows.length;
  const customsEntered = ergebnis.rows.filter((row) => hasValue(row.it.customsInvoice));
  const freightEntered = ergebnis.rows.filter((row) => hasValue(row.it.freightInvoice));
  const customsArticleSum = customsEntered.reduce(
    (sum, row) => sum + (Number(row.it.customsInvoice) || 0),
    0,
  );
  const freightArticleSum = freightEntered.reduce(
    (sum, row) => sum + (Number(row.it.freightInvoice) || 0),
    0,
  );
  const customsInvoice = Number(o.customsInvoice) || 0;
  const freightInvoice = Number(o.freightInvoice) || Number(o.freight) || 0;
  const customsDelta = Math.round((customsArticleSum - customsInvoice) * 100) / 100;
  const freightDelta = Math.round((freightArticleSum - freightInvoice) * 100) / 100;
  const customsOk =
    customsInvoice > 0 && customsEntered.length === articleCount && Math.abs(customsDelta) <= 0.02;
  const freightOk =
    freightInvoice > 0 && freightEntered.length === articleCount && Math.abs(freightDelta) <= 0.02;
  const alleBelege = (o.pdfs || []) as OrderDocument[];
  const customsDocs = alleBelege.filter((doc) => ZOLL_BELEGTYPEN.includes(doc.type as string));
  const freightDocs = alleBelege.filter((doc) => TRANSPORT_BELEGTYPEN.includes(doc.type as string));
  const receivedDate = (o.ekaReceivedDate as string) || "";
  const bookingSuggestion =
    (o.ekaBookingDate as string) || receivedDate || todayIso();

  // `index.html:8733` - EIN Textknoten, wie im Bestand aus einem Template zusammengesetzt.
  const hinweisMenge =
    `Berechnete Menge: ${NUM.format(qty)} Stk.` +
    (focQty ? ` · davon FOC: ${NUM.format(focQty)} Stk.` : "") +
    " · Rechnungen und Kurse direkt je Artikel";

  return (
    <>
      <div className="calc-hero">
        <div>
          <div className="calc-eyebrow">Einkaufskosten</div>
          <div className="calc-title">Einkaufspreis je Artikel</div>
          <div className="calc-hint">{hinweisMenge}</div>
          {focVerlust > 0 ? (
            <div className="calc-note warn" role="status">
              FOC-Menge konnte nicht vollständig zugeordnet werden: {NUM.format(focVerlust)} Stk.
              fehlen in der Kostenverteilung. Bitte die FOC-Positionen und ihre Haupt-SKU prüfen.
            </div>
          ) : (
            ""
          )}
        </div>
        <button className="btn-jtl" type="button" onClick={() => onJtlUebertragen?.()}>
          In JTL übertragen
        </button>
      </div>

      <div className="eka-control-panel">
        <div className="eka-control-head">
          <div>
            <h3>Automatische Dokumentkontrolle</h3>
            <p>Ausgelesene Rechnungen werden gegen die einzelnen Artikelanteile geprüft.</p>
          </div>
          <span className={`fx-health ${customsOk && freightOk ? "ok" : "warn"}`}>
            {customsOk && freightOk ? "Kontrolle vollständig" : "Bestätigung offen"}
          </span>
        </div>
        <div className="eka-control-grid">
          <div className={`eka-check ${customsOk ? "ok" : "warn"}`}>
            <strong>Zollrechnung ↔ Artikel</strong>
            <span>
              {customsDocs.length
                ? `${customsDocs.length} Beleg${customsDocs.length === 1 ? "" : "e"} ausgelesen`
                : "Zollbeleg fehlt"}
              <br />
              {`${EUR.format(customsArticleSum)} / ${EUR.format(customsInvoice)}` +
                (customsInvoice ? ` · Δ ${EUR.format(customsDelta)}` : "")}
            </span>
          </div>
          <div className={`eka-check ${freightOk ? "ok" : "warn"}`}>
            <strong>Transportrechnung ↔ Artikel</strong>
            <span>
              {freightDocs.length
                ? `${freightDocs.length} Beleg${freightDocs.length === 1 ? "" : "e"} ausgelesen`
                : "Transportbeleg fehlt"}
              <br />
              {`${EUR.format(freightArticleSum)} / ${EUR.format(freightInvoice)}` +
                (freightInvoice ? ` · Δ ${EUR.format(freightDelta)}` : "")}
            </span>
          </div>
          <div className={`eka-check ${o.ekaReceivedDateConfirmed ? "ok" : "warn"}`}>
            <strong>Belegeingang</strong>
            <span>
              {receivedDate ? fmtDate(receivedDate) : "Wird beim Auslesen automatisch gesetzt"}
              <br />
              {o.ekaReceivedDateConfirmed ? "händisch bestätigt" : "Bestätigung erforderlich"}
            </span>
          </div>
        </div>
        <div className="eka-date-confirm">
          <label>
            Eingangsdatum (automatisch)
            <Datumsfeld
              wert={roh(receivedDate)}
              onCommit={(wert) => bestellungPatchen("ekaReceivedDate", wert)}
            />
          </label>
          <label>
            Buchungs-/Fälligkeitsdatum (Vorschlag)
            <Datumsfeld
              wert={roh(bookingSuggestion)}
              onCommit={(wert) => bestellungPatchen("ekaBookingDate", wert)}
            />
          </label>
          <label className="eka-received-confirm">
            <input
              type="checkbox"
              checked={!!o.ekaReceivedDateConfirmed}
              onChange={(e) => belegeingangBestaetigen(e.currentTarget.checked)}
            />{" "}
            Exaktes Eingangsdatum händisch bestätigt
          </label>
        </div>
      </div>

      <Abschnitt schluessel="calc-items" titel="EK je Artikel">
        <div className="field-grid calc-source-grid">
          <div className="field">
            <label>Speditionsrechnung netto gesamt (€)</label>
            <input
              className="mono"
              type="number"
              min="0"
              step="0.01"
              value={roh(o.freightInvoice)}
              placeholder="0,00"
              onChange={(e) => bestellungPatchen("freightInvoice", e.currentTarget.value)}
            />
          </div>
          <div className="field">
            <label>Zollrechnung gesamt (€, Fallback)</label>
            <input
              className="mono"
              type="number"
              min="0"
              step="0.01"
              value={roh(o.customsInvoice)}
              placeholder="0,00"
              onChange={(e) => bestellungPatchen("customsInvoice", e.currentTarget.value)}
            />
          </div>
          <div className="field">
            <label>Werkzeugkosten gesamt (€)</label>
            <input
              className="mono"
              type="number"
              min="0"
              step="0.01"
              value={roh(o.toolingCost)}
              placeholder="0,00"
              onChange={(e) => bestellungPatchen("toolingCost", e.currentTarget.value)}
            />
          </div>
          <div className="field">
            <label>Speditionsgebühren (optional)</label>
            <input
              className="mono"
              type="number"
              min="0"
              step="0.01"
              value={roh(o.freightFees)}
              placeholder="0,00"
              onChange={(e) => bestellungPatchen("freightFees", e.currentTarget.value)}
            />
          </div>
        </div>
        <div className="calc-note">
          Fracht, optionale Speditionsgebühren und Werkzeugkosten werden automatisch nach dem
          Warenwert der Artikel verteilt. Zoll wird bei gleicher Zolltarif-Nr. innerhalb der
          Tarifgruppe nach Warenwert aufgeteilt; ein direkt eingetragener Artikelanteil hat Vorrang.
        </div>
        {/* `#calcItems` samt der Weiche aus `renderCalc` (index.html:10389-10391). */}
        <div id="calcItems">
          {ergebnis.rows.length ? (
            <PositionsTabelle
              ergebnis={ergebnis}
              darfBearbeiten={darfBearbeiten}
              onPosition={positionPatchen}
            />
          ) : (
            <div className="empty-note">
              Noch keine Artikel – Positionen im Tab „Bestellung" anlegen.
            </div>
          )}
        </div>
        <div className="calc-note">
          Separate FOC-Positionen werden automatisch der Haupt-SKU zugerechnet und nicht als eigener
          Artikel kalkuliert. Die Menge aus der Packing List hat bei der Hauptposition Vorrang vor
          der Bestellmenge; zugeordnete Gratisstücke werden anschließend addiert.
        </div>
      </Abschnitt>
    </>
  );
}
